/** Formatação de itens para a UI (ícones, nomes, atributos). Pure — sem DOM. */
import { GameConfig } from "../config/GameConfig";
import { definitionById, isWeaponDef } from "./Catalog";
import { i18n } from "../i18n/i18n";
import type { AttrKey, EquipmentInstance, WeaponType } from "./EquipmentTypes";
import { routeDamageType, attrToType, typeToAttrLabel } from "./DamageRoute";

const WEAPON_ICON: Record<string, string> = { sword: "⚔️", axe: "🪓", bow: "🏹", mace: "🔨", staff: "🪄" };
const SLOT_ICON: Record<string, string> = { head: "⛑️", chest: "🛡️", gloves: "🧤", boots: "🥾", accessory: "💍" };

export function itemIcon(inst: EquipmentInstance): string {
  const def = definitionById(inst.definitionId);
  if (!def) return "❔";
  if (isWeaponDef(def)) return WEAPON_ICON[def.weaponType] ?? "⚔️";
  return SLOT_ICON[def.slot] ?? "🎁";
}

export function itemName(inst: EquipmentInstance): string {
  const def = definitionById(inst.definitionId);
  return def ? i18n.t(def.nameKey) : inst.definitionId;
}

export function rarityName(inst: EquipmentInstance): string {
  return i18n.t(`rarity.${inst.rarity}`);
}

const PERCENT: ReadonlySet<AttrKey> = new Set(["attackSpeed", "critChance", "critDamage", "moveSpeed", "dmgSlash", "dmgPierce", "dmgBlunt", "defSlash", "defPierce", "defBlunt", "block", "maxHpPct", "damagePct"]); // 10.9FR

// 10.9CP — classe ativa (o Game seta no recalc): rótulos de tipo de dano se adaptam a ela.
let displayClass: string | null = null;
export function setDisplayClass(classId: string | null): void { displayClass = classId; }

export function attrLabel(key: AttrKey): string {
  // afixo de tipo de dano físico → rótulo do dano REAL da classe (mago vê "Dano Arcano", etc.)
  const native = attrToType(key);
  if (native) return i18n.t(typeToAttrLabel(routeDamageType(native, displayClass)));
  return i18n.t(`equipment.attr.${key}`);
}

export function formatAttrValue(key: AttrKey, value: number): string {
  if (key === "regen") return `+${(value * 100).toFixed(1)}%/s`; // 10.5T
  if (key === "lifesteal") return `+${(value * 100).toFixed(1)}%`; // 10.9EQ — % do dano causado
  if (PERCENT.has(key)) return `+${Math.round(value * 100)}%`;
  if (key === "attackRange") return `+${value.toFixed(1)}m`;
  return `+${Math.round(value)}`;
}

/** Descrição curta de um bônus de maestria (numérico ou trait). */
export function masteryBonusDesc(bonus: Record<string, number | string>): string {
  const parts: string[] = [];
  for (const k of Object.keys(bonus)) {
    const v = bonus[k];
    if (k === "trait" && typeof v === "string") parts.push(i18n.t(`mastery.trait.${v}`));
    else if (typeof v === "number") {
      if (k === "attackSpeedMult") parts.push(`+${Math.round(v * 100)}% ${i18n.t("equipment.attr.attackSpeed")}`);
      else if (k === "damageMult") parts.push(`+${Math.round(v * 100)}% ${i18n.t("equipment.attr.damage")}`);
      else if (k === "critChance") parts.push(`+${Math.round(v * 100)}% ${i18n.t("equipment.attr.critChance")}`);
      else if (k === "critDamageMult") parts.push(`+${Math.round(v * 100)}% ${i18n.t("equipment.attr.critDamage")}`);
      else if (k === "attackRange") parts.push(`+${v}m ${i18n.t("equipment.attr.attackRange")}`);
      else if (k === "projectileSpeedMult") parts.push(`+${Math.round(v * 100)}% proj.`);
    }
  }
  return parts.join(", ");
}

/** Próximo marco de maestria acima do nível atual (ou null se no teto). */
export function nextMasteryReward(type: WeaponType, level: number): { level: number; desc: string } | null {
  const table = GameConfig.mastery.bonuses[type] as Record<string, Record<string, number | string>>;
  let best = Infinity;
  for (const k of Object.keys(table)) {
    const lv = Number(k);
    if (lv > level && lv < best) best = lv;
  }
  if (!Number.isFinite(best)) return null;
  return { level: best, desc: masteryBonusDesc(table[String(best)]) };
}
